import { User } from 'firebase/auth'
import { QuerySnapshot } from 'firebase/firestore'
import create from 'zustand'
import { PANASReport } from '../pages/Report/Step-3'
import Doctor from '../types/Doctor'
import FirestoreUser, {
  QuestionnaireInterface,
  StageStatus,
} from '../types/FirestoreUser'
import { IncidentSubmission, VitalStats } from '../types/MonthlyReport'
import { Answer } from '../types/Questionnaire'
import { Regimen } from '../types/Regimen'
import { Stage } from '../types/Stage'
import { StoolQuestionnaire } from '../types/StoolQuestionnaire'
import {
  AlsClinicAttendance,
  EmotionalSupportPerson,
  MedicalSupportMonth,
} from '../types/support'
import {
  ActorIdentity,
  EffectiveIdentity,
  IdentityStatus,
  VerifiedImpersonationSession,
} from '../types/Identity'

export type LocalConfig = {
  [docId: string]: {
    [itemId: string]: string
  }
}

export type MonthlyReport = {
  protocols?: boolean
  diets?: boolean
  physicalStrategies?: boolean
  mentalStrategies?: boolean
  supplements?: boolean
  nonSupplementMedications?: boolean
  /** @deprecated Kept for historical reads. Use medicalSupportListIsAccurate instead. */
  noMedicalProfessionalHours?: boolean
  supplementListIsAccurate?: boolean
  medicationListIsAccurate?: boolean
  physicalStrategyListIsAccurate?: boolean
  dentalListIsAccurate?: boolean
  emotionalSupportListIsAccurate?: boolean
  clinicalObservations?: string
  alsfrs?: MonthlyReportQuestionnaire
  alssq?: MonthlyReportQuestionnaire
  vitalStats?: Partial<VitalStats>
  panas?: PANASReport
  stool?: {
    stool: Partial<StoolQuestionnaire['stool']>
    urine: Partial<StoolQuestionnaire['urine']>
  }
  support?: boolean
  emotionalSupportPeople?: EmotionalSupportPerson[]
  removedEmotionalSupportPeopleKeys?: string[]
  medicalSupport?: MedicalSupportMonth
  removedDoctorIds?: string[]
  medicalSupportListIsAccurate?: boolean
  alsClinic?: AlsClinicAttendance
  incidents?: IncidentSubmission
  stages?: boolean
  stageProgress?: {
    [key: string]: StageStatus
  }
  sundays?: Record<string, 'live' | 'recording' | 'none'>
  educationalHours?: Record<string, number>
  waterType?: keyof typeof waterTypes
  waterAmount?: number
  waterConfirmed?: boolean
  macros?: Macros
  macrosConfirmed?: boolean
  carbStyleDiet?: CarbStyleDiet
  dairyStyleDiet?: DairyStyleDiet
  grainStyleDiet?: GrainStyleDiet
  allStepsCompleted?: boolean
}

export const waterTypes = {
  tapUnfiltered: 'Tap Unfiltered',
  tapFiltered: 'Tap Filtered',
  wellUnfiltered: 'Well Unfiltered',
  wellFiltered: 'Well Filtered',
  bottledSpring: 'Bottled Spring',
  bottledPurified: 'Bottled Purified',
  alkaline: 'Alkaline',
  reverseOsmosis: 'Reverse Osmosis',
  naturalSpring: 'Spring water from a natural spring',
}

export type Macros = {
  protienGrams?: number
  fatGrams?: number
  carbGrams?: number
}

export type CarbStyleDiet =
  | 'carnivore'
  | 'carnivoreLion'
  | 'ketovore'
  | 'ketogenic'
  | 'limitedCarb'
  | 'lowCarb'
  | 'moderateCarb'
  | 'unrestrictedCarb'

export type DairyStyleDiet =
  | 'gheeCreamOnly'
  | 'gheeCreamButterOnly'
  | 'gheeCreamButterCheeseOnly'
  | 'dairy'
  | 'noDairyNoWhey'

export type GrainStyleDiet =
  | 'glutenFree'
  | 'grainFreeCornFree'
  | 'grainStrictlyLimited'
  | 'consumedGlutenGrains'

export type MonthlyReportQuestionnaire = {
  score?: number
  answers: Record<string, Answer>
}

export type Reports = Partial<Record<string, MonthlyReport>>

const useStore = create<{
  user?: User | null
  identityStatus: IdentityStatus
  actorIdentity?: ActorIdentity
  effectiveIdentity?: EffectiveIdentity
  impersonationSession?: VerifiedImpersonationSession
  firestoreUser?: FirestoreUser
  setQuestionnaireInterface: (
    questionnaireInterface: QuestionnaireInterface
  ) => void
  config?: LocalConfig
  regimens: Regimen[]
  reports: Reports
  doctors?: QuerySnapshot<Doctor>
  stages?: Stage[]
  meta?: {
    dayOfMonthLimit: number
    omitSundays: string[]
    numBootcamps: number
    bootcampNames: string[]
    numEssentialsCourses: number
    essentialsCoursesNames: string[]
  }
  content?: {
    purpleBubbles?: {
      contentBlocks: { id: string; text: string }[]
    }
    [key: string]: any
  }
}>((set) => {
  return {
    identityStatus: 'initializing',
    regimens: [],
    reports: {},
    content: {
      purpleBubbles: {
        contentBlocks: [],
      },
    },
    setQuestionnaireInterface: (questionnaireInterface) => {
      set((state) => ({
        firestoreUser: state.firestoreUser
          ? { ...state.firestoreUser, questionnaireInterface }
          : undefined,
      }))
    },
  }
})

export const resetStore = () => {
  useStore.setState({
    firestoreUser: undefined,
    regimens: [],
    reports: {},
  })
}

export default useStore
